// OrderExplainerDrawer.tsx — Right-side sliding panel for Order Explainer

import React, { useCallback, useEffect } from "react";
import { Drawer, Spin } from "antd";
import { CloseOutlined, LoadingOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
	getOrderExplanation,
	getOrderExplanationLoading,
	getOrderExplanationError,
} from "models/orderExplainer/selector";
import { closeOrderExplainerDrawer } from "models/orderExplainer/reducer";
import { fetchOrderExplanation } from "models/orderExplainer/sagaActions";
import "./orderExplainerDrawer.scss";

const OrderExplainerDrawer: React.FC = () => {
	const dispatch = useDispatch();
	const drawerOpen = useSelector((state: any) => state["model/orderExplainer"]?.drawerOpen ?? false);
	const orderDetailId = useSelector((state: any) => state["model/orderExplainer"]?.drawerOrderDetailId);
	const rowData = useSelector((state: any) => state["model/orderExplainer"]?.drawerRowData ?? null);

	const explanationData = useSelector((state: any) =>
		orderDetailId ? getOrderExplanation(state, orderDetailId) : null
	);
	const loading = useSelector((state: any) =>
		orderDetailId ? getOrderExplanationLoading(state, orderDetailId) : false
	);
	const error = useSelector((state: any) =>
		orderDetailId ? getOrderExplanationError(state, orderDetailId) : null
	);

	useEffect(() => {
		if (drawerOpen && orderDetailId && !explanationData && !loading && !error) {
			dispatch(fetchOrderExplanation({ orderDetailId }));
		}
	}, [drawerOpen, orderDetailId, explanationData, loading, error, dispatch]);

	const handleClose = useCallback(() => {
		dispatch(closeOrderExplainerDrawer());
	}, [dispatch]);


	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape" && drawerOpen) {
				handleClose();
			}
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [drawerOpen, handleClose]);

	if (!drawerOpen) return null;

	// Grid rows may store values as { value, isUpdated } wrappers.
	// Unwrap them so we always render primitives, not objects.
	const unwrap = (val: any) =>
		val && typeof val === "object" && "value" in val ? val.value : val;

	const apiSku = explanationData?.sku;
	const apiInventory = explanationData?.inventory;
	const apiWos = explanationData?.wos;
	const apiOrder = explanationData?.order;
	const apiConstraints = explanationData?.constraints;

	const storeLabel = explanationData?.store?.store

	const skuLabelParts: any[] = [];
	if (apiSku?.sku) skuLabelParts.push(apiSku.sku);
	if (apiSku?.description) skuLabelParts.push(apiSku.description);
	if (!skuLabelParts.length) {
		if (unwrap(rowData?.SKU)) skuLabelParts.push(unwrap(rowData?.SKU));
		if (unwrap(rowData?.Description || rowData?.Item))
			skuLabelParts.push(unwrap(rowData?.Description || rowData?.Item));
	}
	const skuDesc = skuLabelParts.join(" - ") || "—";

	const orderQty = explanationData?.parameters?.itemQtyAfter ?? unwrap(rowData?.OrderQty);
	const suggestedQty =
		apiOrder?.suggested ??
		apiOrder?.initialSuggested ??
		unwrap(rowData?.SuggestedQty);
	const onHand = apiInventory?.onHand ?? unwrap(rowData?.OnHand);
	const available = explanationData?.parameters?.availableQty ?? unwrap(rowData?.Current_Available_Qty);
	const wos = apiWos?.current ?? unwrap(rowData?.WOS);
	const wosTarget = apiWos?.target ?? unwrap(rowData?.WOSTarget);
	const shipMultiple =
		apiConstraints?.shipMultiple ?? unwrap(rowData?.ShipMultiple);

	const fillRate =
		orderQty != null && suggestedQty != null && suggestedQty > 0
			? Math.round((Number(orderQty) / Number(suggestedQty)) * 100)
			: null;

	let fillRateClass = "";
	if (fillRate === 100) {
		fillRateClass = "order-explainer-panel__metric-value--success";
	} else if (fillRate != null && fillRate < 50) {
		fillRateClass = "order-explainer-panel__metric-value--danger";
	}

	return (
		<Drawer
			open={drawerOpen}
			onClose={handleClose}
			placement="right"
			width={380}
			closable={false}
			className="order-explainer-drawer"
			bodyStyle={{ padding: 0 }}
		>
			<div className="order-explainer-panel">
				<div className="order-explainer-panel__header">
					<span className="order-explainer-panel__title">
						<ThunderboltOutlined /> Order Qty
					</span>
					<button
						type="button"
						className="order-explainer-panel__close"
						onClick={handleClose}
						aria-label="Close"
					>
						<CloseOutlined />
					</button>
				</div>

				<div className="order-explainer-panel__body">
					{/* Item info */}
					<div className="order-explainer-panel__item">
						<span className="order-explainer-panel__item-label">{storeLabel} {explanationData?.store?.customer}</span>
						<span className="order-explainer-panel__item-sku">{skuDesc}</span>
					</div>

					{/* Descriptive text */}
					{loading && (
						<div className="order-explainer-panel__loading">
							<Spin indicator={<LoadingOutlined spin />} size="small" />
							<span>Loading explanation...</span>
						</div>
					)}
					{!loading && explanationData?.tooltipText && (
						<p className="order-explainer-panel__text">{explanationData.tooltipText}</p>
					)}
					{!loading && error && (
						<p className="order-explainer-panel__text order-explainer-panel__text--error">
							{error}
						</p>
					)}
					{!loading && !explanationData && !error && (
						<p className="order-explainer-panel__text order-explainer-panel__text--empty">
							No explanation available.
						</p>
					)}

					{/* Order metrics */}
					<div className="order-explainer-panel__section">
						<div className="order-explainer-panel__section-title">ORDER METRICS</div>
						<div className="order-explainer-panel__metrics">
							<div className="order-explainer-panel__metric">
								<span className="order-explainer-panel__metric-label">ORDER QTY</span>
								<span className="order-explainer-panel__metric-value order-explainer-panel__metric-value--primary">
									{orderQty ?? "—"}
								</span>
							</div>
							<div className="order-explainer-panel__metric">
								<span className="order-explainer-panel__metric-label">SUGGESTED</span>
								<span className="order-explainer-panel__metric-value">
									{suggestedQty ?? "—"}
								</span>
							</div>
							<div className="order-explainer-panel__metric">
								<span className="order-explainer-panel__metric-label">FILL RATE</span>
								<span
									className={`order-explainer-panel__metric-value ${fillRateClass}`}
								>
									{fillRate != null ? `${fillRate}%` : "—"}
								</span>
							</div>
							<div className="order-explainer-panel__metric">
								<span className="order-explainer-panel__metric-label">AVAILABLE</span>
								<span className="order-explainer-panel__metric-value">
									{available ?? "—"}
								</span>
							</div>
						</div>
					</div>

					{/* Inventory & Demand */}
					<div className="order-explainer-panel__section">
						<div className="order-explainer-panel__section-title">INVENTORY & DEMAND</div>
						<div className="order-explainer-panel__grid">
							<div className="order-explainer-panel__grid-item">
								<span className="order-explainer-panel__grid-label">On Hand</span>
								<span className="order-explainer-panel__grid-value">{onHand ?? "—"}</span>
							</div>
							<div className="order-explainer-panel__grid-item">
								<span className="order-explainer-panel__grid-label">Current WOS</span>
								<span className="order-explainer-panel__grid-value">
									{wos != null ? `${Number(wos).toFixed(1)} wk` : "—"}
								</span>
							</div>
							<div className="order-explainer-panel__grid-item">
								<span className="order-explainer-panel__grid-label">Target WOS</span>
								<span className="order-explainer-panel__grid-value">
									{wosTarget != null ? `${Number(wosTarget).toFixed(1)} wk` : "—"}
								</span>
							</div>
							<div className="order-explainer-panel__grid-item">
								<span className="order-explainer-panel__grid-label">Ship Multiple</span>
								<span className="order-explainer-panel__grid-value">
									{shipMultiple ?? "—"}
								</span>
							</div>
							<div className="order-explainer-panel__grid-item">
								<span className="order-explainer-panel__grid-label">Weekly Forecast</span>
								<span className="order-explainer-panel__grid-value">
									{explanationData?.demand?.weeklyForecast ?? "—"}
								</span>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Drawer>
	);
};

export default OrderExplainerDrawer;
